import React from "react";
import { Navigate, useParams } from "react-router-dom";

import {
  UserOutlined,
  DashboardOutlined, LoginOutlined, MailOutlined, SafetyCertificateOutlined,
  TeamOutlined, EditOutlined, UserAddOutlined, CustomerServiceOutlined,
  ApartmentOutlined, FileTextOutlined, FileOutlined, FormOutlined, BarChartOutlined,
  CommentOutlined, DatabaseOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { IoMdCloudUpload } from "react-icons/io";
import { FaVault } from "react-icons/fa6";

// Auth screens
import RegisterScreen from "../screens/RegisterScreen";
import LoginScreen from "../screens/LoginScreen";
import DashboardScreen from "../screens/dashboard/DashboardScreen";
import ResetPasswordScreen from "../screens/ResetPasswordScreen";
import ResetPasswordSentScreen from "../screens/ResetPasswordSentScreen";
import ResetPasswordSetupScreen from "../screens/ResetPasswordSetupScreen";
import TwoFACodeRequestScreen from "../screens/TwoFACodeRequestScreen";
import EmailConfirmedScreen from "../screens/EmailConfirmedScreen";

// Identity user
import EditUserScreen from "../screens/users/EditUserScreen";
import UserManagementScreen from "../screens/users/UserManagementScreen";
import CreateUserScreen from "../screens/users/CreateUserScreen";
import ApplicationUserProfileScreen from "../screens/users/ApplicationUserProfileScreen";
import FeedbackManagementScreen from "../screens/users/FeedbackManagementScreen";

// Patient screens
import PatientManagementScreen from "../screens/patients/PatientManagementScreen";
import PatientDetailScreen from "../screens/patients/PatientDetailScreen";
import CohortManagementScreen from "../screens/patients/CohortManagementScreen";
import SubjectListScreen from "../screens/patients/SubjectListScreen";
import SubjectDetailScreen from "../screens/patients/SubjectDetailScreen";
import UploadBatchListScreen from "../screens/patients/UploadBatchListScreen";

// Identifier Vault screens
import IdentifierVaultScreen from "../screens/identifierVault/IdentifierVaultScreen";
import IdentifierVaultParticipantScreen from "../screens/identifierVault/IdentifierVaultParticipantScreen";

// Dynamic Forms screens
import FormsListScreen from "../screens/forms/FormsListScreen";
import FormCreateScreen from "../screens/forms/FormCreateScreen";
import FormEditScreen from "../screens/forms/FormEditScreen";

// Template screens
import DocumentTemplateListScreen from "../screens/template/DocumentTemplateListScreen";
import DocumentTemplateCreateScreen from "../screens/template/DocumentTemplateCreateScreen";
import DocumentTemplateUpdateScreen from "../screens/template/DocumentTemplateUpdateScreen";

// Reporting screen
import ReportingScreen from "../screens/reporting/ReportingScreen";

// Unauthorized
import UnauthorizedScreen from "../screens/UnauthorizedScreen";

// Not Found
import NotFoundScreen from "../screens/NotFoundScreen";

// Business Organisation
import BusinessOrganisationManagementScreen from "../screens/organisations/BusinessOrganisationManagementScreen";
import BusinessOrganisationDataManagementScreen from "../screens/organisations/BusinessOrganisationDataManagementScreen";

// Control Panel
import ControlPanelScreen from "../screens/settings/controlPanel/ControlPanelScreen";

// Generic Template Management
import TemplateManagementScreen from "../screens/settings/TemplateManagementScreen";
import SystemTemplateManagementScreen from "../screens/settings/SystemTemplateManagementScreen";

// SMTP Config
import SmtpConfigScreen from "../screens/settings/SmtpConfigScreen";

// ApplicationUser Management
import ApplicationUserManagementScreen from "../screens/applicationUser/ApplicationUserManagementScreen";

// Data Management
import DataManagementScreen from "../screens/settings/DataManagementScreen";

// Upload Session Settings
import UploadSessionsSettingsScreen from "../screens/settings/UploadSessionsSettingsScreen";

import PortalLayout from "../components/layout/PortalLayout.component";
import PortalHomeScreen from "../screens/portal/PortalHomeScreen";
import PortalPostsScreen from "../screens/portal/PortalPostsScreen";
import PortalPostDetailScreen from "../screens/portal/PortalPostDetailScreen";
import PortalPostManagementScreen from "../screens/settings/PortalPostManagementScreen";
import PortalPostFormScreen from "../screens/settings/PortalPostFormScreen";

const LegacyFormsEditRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/settings/forms/edit/${id}`} replace />;
};

// super admin routes
const superUserRoutes = [
  // Control Panel
  { path: '/settings', component: ControlPanelScreen, searchLabel: 'Control Panel', searchable: true },
  // User Management
  { path: '/settings/user_management', component: UserManagementScreen, searchLabel: 'User Management', searchable: true, icon: <TeamOutlined /> },
  // User Create
  { path: '/settings/users/create', component: CreateUserScreen, searchLabel: 'Create User', searchable: false, icon: <UserAddOutlined /> },
  // Profile
  { path: '/settings/users/:_id', component: EditUserScreen, searchLabel: 'Edit User Profile', searchable: false, icon: <EditOutlined /> },
  // Organisation Management
  { path: '/settings/business_organisation_management', component: BusinessOrganisationManagementScreen, searchLabel: 'Business Organisation Management', searchable: true, icon: <ApartmentOutlined /> },
  // Organisation Data Management
  { path: '/settings/business_organisation_data_management', component: BusinessOrganisationDataManagementScreen, searchLabel: 'Data Sharing Agreement Compliance Dashboard', searchable: true, icon: <FileTextOutlined /> },
  // Generic Template Management
  { path: '/settings/templates', component: TemplateManagementScreen, searchLabel: 'Generic Template Management', searchable: true, icon: <FileOutlined /> },
  { path: '/settings/system_templates', component: SystemTemplateManagementScreen, searchLabel: 'System Templates', searchable: true, icon: <FormOutlined /> },
  { path: '/settings/applicationUsers', component: ApplicationUserManagementScreen, searchLabel: 'ApplicationUser Management', searchable: true, icon: <UserOutlined /> },
  // SMTP Config
  { path: '/settings/smtp_config', component: SmtpConfigScreen, searchLabel: 'SMTP Config', searchable: true, icon: <MailOutlined /> },
  // Feedback Management
  { path: '/settings/feedback', component: FeedbackManagementScreen, searchLabel: 'Feedback Management', searchable: true, icon: <CommentOutlined /> },
  // Data Management
  { path: '/settings/data_management', component: DataManagementScreen, searchLabel: 'Data Management', searchable: true, icon: <DatabaseOutlined /> },
  // Upload Session Settings
  { path: '/settings/upload_sessions', component: UploadSessionsSettingsScreen, searchLabel: 'Upload Session Settings', searchable: true, icon: <IoMdCloudUpload /> },
  // Dynamic Forms (form builder, attached to Patient)
  { path: '/settings/forms', component: FormsListScreen, searchLabel: 'Dynamic Forms', searchable: true, icon: <FileTextOutlined /> },
  { path: '/settings/forms/create', component: FormCreateScreen, searchLabel: 'Create Form', searchable: false, icon: <FileTextOutlined /> },
  { path: '/settings/forms/edit/:id', component: FormEditScreen, searchLabel: 'Edit Form', searchable: false, icon: <EditOutlined /> },
  { path: '/settings/portal_posts', component: PortalPostManagementScreen, searchLabel: 'Portal Posts', searchable: true, icon: <FileTextOutlined /> },
  { path: '/settings/portal_posts/create', component: PortalPostFormScreen, searchLabel: 'New Portal Post', searchable: false, icon: <FileTextOutlined /> },
  { path: '/settings/portal_posts/edit/:id', component: PortalPostFormScreen, searchLabel: 'Edit Portal Post', searchable: false, icon: <EditOutlined /> },
];

// Private routes
const authenticatedUserRoutes = [
  // Dashboard
  { path: "/user_dashboard", component: DashboardScreen, icon: <DashboardOutlined /> },
  // Overview
  { path: "/dashboard", component: DashboardScreen, icon: <DashboardOutlined /> },
  // Subjects (renamed from Cohorts)
  { path: "/subjects", component: SubjectListScreen, searchLabel: "Subjects", searchable: true, icon: <TeamOutlined /> },
  // Subject Detail/Edit
  { path: "/subjects/:id/edit", component: SubjectDetailScreen, searchLabel: "Edit Subject", searchable: false, icon: <EditOutlined /> },
  { path: "/subjects/new", component: SubjectDetailScreen, searchLabel: "Create Subject", searchable: false, icon: <UserAddOutlined /> },
  // Upload Batch
  { path: "/upload_batches", component: UploadBatchListScreen, searchLabel: "Upload Batches", searchable: true, icon: <IoMdCloudUpload /> },
  // Redirect to /user_dashboard by default
  { path: "/", exact: true, component: () => <Navigate to="/dashboard" replace />, icon: <DashboardOutlined /> },
  { path: "/unauthorized", component: UnauthorizedScreen },
  // Patient
  { path: "/patients", component: PatientManagementScreen, searchLabel: "Patient List", searchable: true, icon: <CustomerServiceOutlined /> },
  { path: "/patients/create", component: PatientDetailScreen, searchLabel: "Create Patient", searchable: false, icon: <UserAddOutlined /> },
  { path: "/patients/:patientId", component: PatientDetailScreen, searchLabel: "Edit Patient", searchable: false, icon: <EditOutlined /> },
  // Upload Sessions
  { path: "/cohorts", component: CohortManagementScreen, searchLabel: "Upload Session Management", searchable: true, icon: <TeamOutlined /> },
  // Identifier Vault — cross-organisation participant identity (protocol §8); data controller only
  { path: "/identifier_vault", component: IdentifierVaultScreen, searchLabel: "Identifier Vault", searchable: true, icon: <FaVault /> },
  { path: "/identifier_vault/:participantId", component: IdentifierVaultParticipantScreen, searchLabel: "Vault Participant", searchable: false, icon: <FaVault /> },
  // Legacy Dynamic Forms paths → settings
  { path: "/forms", component: () => <Navigate to="/settings/forms" replace /> },
  { path: "/forms/create", component: () => <Navigate to="/settings/forms/create" replace /> },
  { path: "/forms/edit/:id", component: LegacyFormsEditRedirect },
  // Reporting
  { path: "/reporting", component: ReportingScreen, searchLabel: "Reporting", searchable: true, icon: <BarChartOutlined /> },
  // Template routes
  { path: "/document_templates", exact: true, component: DocumentTemplateListScreen, searchLabel: "Document Templates", searchable: true, icon: <FileOutlined /> },
  { path: "/document_templates/create", component: DocumentTemplateCreateScreen, searchLabel: "Create Document Template", searchable: false },
  { path: "/document_templates/:id/edit", component: DocumentTemplateUpdateScreen, searchLabel: "Edit Document Template", searchable: false },
  // ApplicationUser Profile
  { path: "/applicationUser/profile/:id", component: ApplicationUserProfileScreen, searchLabel: "ApplicationUser Profile", searchable: false, icon: <UserOutlined /> },
  { path: "/myprofile", component: ApplicationUserProfileScreen },
];

// Public routes
const publicRoutes = [
  // Authentication pages
  { path: "/usersignup", component: RegisterScreen, icon: <UserAddOutlined /> },
  { path: "/userlogin", component: LoginScreen, icon: <LoginOutlined /> },
  { path: "/user_reset_password", component: ResetPasswordScreen, icon: <SafetyCertificateOutlined /> },
  { path: "/user_reset_passwordsent", component: ResetPasswordSentScreen, icon: <MailOutlined /> },
  { path: "/user_reset_password_setup", component: ResetPasswordSetupScreen, icon: <SafetyCertificateOutlined /> },
  { path: "/2fauth", component: TwoFACodeRequestScreen, icon: <SafetyCertificateOutlined /> },
  { path: "/email_confirmed", component: EmailConfirmedScreen, icon: <MailOutlined /> },
  { path: "/not_found", component: NotFoundScreen },
  { path: "/home", component: PortalHomeScreen, layout: PortalLayout, icon: <GlobalOutlined /> },
  { path: "/posts/:slug", component: PortalPostDetailScreen, layout: PortalLayout },
  { path: "/posts", component: PortalPostsScreen, layout: PortalLayout, icon: <FileTextOutlined /> },
];

export { authenticatedUserRoutes, publicRoutes, superUserRoutes };